import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit'

// The API is served from the same origin as this app - Express serves the
// compiled client out of its own public/ directory - so every call is a
// relative path. That is what allows the session cookie to be SameSite=Strict.
//
// In development the CRA dev server proxies /api to localhost:3001 (see the
// "proxy" field in package.json), so the browser still sees one origin and
// cookies behave exactly as they do in production.
const url = '';

const endPoints = {
    // Authentication
    'auth/login': url + '/api/auth/login',
    'auth/logout': url + '/api/auth/logout',
    'auth/me': url + '/api/auth/me',
    'auth/changePassword': url + '/api/auth/change-password',
    'auth/requestReset': url + '/api/auth/request-reset',
    'auth/completeReset': url + '/api/auth/complete-reset',
    'auth/adminResetPassword': url + '/api/auth/admin/reset-password',

    'pageLayout/getUserTypeAndPermissions': url + '/api/userPermissions',
    'pageLayout/putUser': url + '/api/putUser',
    'DoA/putUserType': url + '/api/userType',
    'DoA/getUserTypes': url + '/api/userTypes',
    'DoA/putUserStatus': url + '/api/userStatus',
    'pageLayout/putUserStatus': url + '/api/userStatus',
    'DoA/getDoA': url + '/api/delegationOfAuthority',
    'DoA/putDoA': url + '/api/delegationOfAuthority',
    'quotesAndNotifications/getUsersByRole': url + '/api/userRoles',
    'quotesAndNotifications/getCustomerInfo': url + '/api/customerInfo',
    'quotesAndNotifications/getProductDescriptions': url + '/api/productDescriptions',
    'quotesAndNotifications/getDiscounts': url + '/api/getDiscounts',
    'quotesAndNotifications/getMachines': url + '/api/machines',
    'quotesAndNotifications/getQuotes': url + '/api/quotes',
    'quotesAndNotifications/getAllQuotes': url + '/api/allQuotes',
    'quotesAndNotifications/getSingleQuote': url + '/api/getQuote',
    'quotesAndNotifications/putQuote': url + '/api/createQuote',
    'quotesAndNotifications/getCategories': url + '/api/modificationCategories',
    'quotesAndNotifications/getNotifications': url + '/api/notifications',
    'quotesAndNotifications/getAllNotifications': url + '/api/getAllNotifications',
    'quotesAndNotifications/putEmailNotification': url + '/api/emailNotifications',
    'quotesAndNotifications/getASMs': url + '/api/asms',
    'quotesAndNotifications/getAllASMs': url + '/api/allAsms',
    'quotesAndNotifications/getMarkets': url + '/api/markets',
    'quotesAndNotifications/authorizationUpload': url + '/api/authorizationUpload',
    'quotesAndNotifications/checkQuoteNumber': url + '/api/checkQuoteNumber',
    'quotesAndNotifications/evaluateDoA': url + '/api/delegationOfAuthority',

    // External PDF service, not part of this API.
    'quotesAndNotifications/printQuote': 'https://mykeysatgf.com/hdmi-pdf/printQuote.php'
};

// Send cookies with every request. Same-origin calls would do this anyway;
// setting it explicitly keeps behaviour correct if the API is ever addressed
// cross-origin during development.
axios.defaults.withCredentials = true;

/**
 * Reads a cookie by name. The CSRF cookie is deliberately readable by script -
 * it carries no authority on its own and is only ever checked against the
 * value held on the server session.
 */
function readCookie(name) {
    const match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Attaches the CSRF token to every state-changing request. This API uses POST
 * for reads as well as writes, so in practice the header goes on nearly every
 * call - which is exactly what the server expects.
 */
axios.interceptors.request.use(config => {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' && method !== 'head') {
        const token = readCookie('hdmi_csrf');
        if (token) {
            config.headers = config.headers || {};
            config.headers['X-CSRF-Token'] = token;
        }
    }
    return config;
});

// The app registers a callback here so a dropped session can be handled in one
// place. Done with a callback rather than importing the store, which would be
// a circular import - the store imports this module.
let onSessionLost = null;

export function setSessionLostHandler(handler) {
    onSessionLost = handler;
}

/**
 * Catches a session that is no longer valid.
 *
 * A session can end at any moment - it idles out, an admin deactivates the
 * account, the password is changed elsewhere - so any call can come back 401.
 * Rather than leaving each caller to handle that, the app is returned to the
 * login screen from here.
 *
 * 403 is deliberately not treated this way: the user is still signed in, they
 * simply may not do that particular thing.
 */
axios.interceptors.response.use(
    response => response,
    error => {
        const status = error.response ? error.response.status : null;
        const requestUrl = error.config ? error.config.url : '';

        // A failed sign-in attempt is a 401 too, and must not be mistaken for
        // an expired session.
        const isLoginAttempt = typeof requestUrl === 'string' && requestUrl.indexOf('/api/auth/login') !== -1;

        if (status === 401 && !isLoginAttempt && onSessionLost) {
            onSessionLost(error.response.data || {});
        }
        if (status === 403 && error.response.data
            && error.response.data.error === 'password_change_required' && onSessionLost) {
            onSessionLost(error.response.data);
        }
        return Promise.reject(error);
    }
);

export function thunkApiCallGenerator(name, method){
    let _method;
    if(method){
        _method = method;
    } else {
        _method = 'POST';
    }
    return createAsyncThunk(
        name,
        async data => {
            const response = await axios({
                method: _method,
                url: endPoints[name],
                data: data
            });
            return response.data;
        }
    );
}

export function apiCall(name, data, method){
    let _method;
    if(method){
        _method = method;
    } else {
        _method = 'POST';
    }
    return axios({
        method: _method,
        url: endPoints[name],
        data: data
    });
}
